<template>
    <div class="requested-bookings-page">
        <v-container fluid class="pa-0">
            <v-row no-gutters>
                <v-col cols="12" class="primary text-left py-1">
                    <v-btn fab color="primary" elevation="0" small @click="$router.go(-1)">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>

        <div ref="scrollContainer" class="requested-bookings-scroll rounded"
            :class="{ 'requested-bookings-scroll--mobile': $vuetify.breakpoint.mobile }" :style="wrapperStyle"
            @scroll="onScroll">
            <v-container class="py-4 py-sm-6">
                <div class="page-head">
                    <div>
                        <div class="page-title">Requested Bookings</div>
                        <div class="page-subtitle">
                            Pending lesson requests. Tap a student name to open their profile.
                        </div>
                    </div>
                    <v-btn outlined color="primary" class="refresh-btn" :loading="loading"
                        :disabled="loading || loadingMore" @click="loadRequests(false)">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                </div>

                <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

                <v-alert v-if="!loading && errorMessage" type="error" dense class="mb-4">
                    {{ errorMessage }}
                </v-alert>

                <v-card v-if="!loading && !requests.length" class="empty-state" flat>
                    <v-card-text class="text-center py-10 py-sm-12">
                        <v-icon size="64" color="grey lighten-1" class="mb-3 mb-sm-4">
                            mdi-inbox-arrow-down-outline
                        </v-icon>
                        <div class="text-subtitle-1 text-sm-h6 grey--text text--darken-1">
                            No pending booking requests.
                        </div>
                        <div class="text-body-2 grey--text mt-2">
                            Requests will appear here when students ask to book lessons.
                        </div>
                    </v-card-text>
                </v-card>

                <template v-else-if="!loading && requests.length">
                    <v-card v-for="req in requests" :key="req.id" class="request-card request-card-horizontal mb-4"
                        outlined>
                        <v-card-text class="pa-4">
                            <div class="request-card-layout">
                                <div class="request-card-layout__identity">
                                    <div class="d-flex align-start">
                                        <router-link v-if="req.studentId"
                                            :to="{ name: 'StudentProfile', params: { id: req.studentId } }"
                                            class="avatar-link mr-3 flex-shrink-0"
                                            :aria-label="`Open profile for ${req.studentName}`">
                                            <v-avatar size="56" class="request-avatar">
                                                <img v-if="req.studentAvatar" :src="req.studentAvatar"
                                                    :alt="req.studentName" />
                                                <v-icon v-else color="primary">mdi-account-circle</v-icon>
                                            </v-avatar>
                                        </router-link>
                                        <v-avatar v-else size="56" class="mr-3 flex-shrink-0">
                                            <img v-if="req.studentAvatar" :src="req.studentAvatar"
                                                :alt="req.studentName" />
                                            <v-icon v-else color="primary">mdi-account-circle</v-icon>
                                        </v-avatar>
                                        <div class="min-width-0 flex-grow-1">
                                            <div class="request-title-row">
                                                <router-link v-if="req.studentId"
                                                    :to="{ name: 'StudentProfile', params: { id: req.studentId } }"
                                                    class="request-student-name request-student-link">
                                                    {{ req.studentName }}
                                                </router-link>
                                                <span v-else class="request-student-name">{{ req.studentName }}</span>
                                                <v-chip x-small color="warning" text-color="black"
                                                    class="ml-2 flex-shrink-0">
                                                    Pending
                                                </v-chip>
                                            </div>
                                            <div v-if="req.studentEmail"
                                                class="request-email text-caption grey--text text--darken-1 mt-1">
                                                <v-icon x-small class="mr-1">mdi-email-outline</v-icon>
                                                {{ req.studentEmail }}
                                            </div>
                                            <div class="request-meta mt-1">
                                                <span class="meta-item">
                                                    <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                                                    {{ req.hoursLesson }} hour(s)
                                                </span>
                                                <span class="meta-dot">•</span>
                                                <span class="meta-item">
                                                    <v-icon x-small class="mr-1">mdi-tag-outline</v-icon>
                                                    booking_lesson
                                                </span>
                                            </div>
                                            <div class="request-created mt-1 grey--text text--darken-1">
                                                Requested {{ formatWhen(req.createdAt) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="request-card-layout__notes">
                                    <div class="notes-block">
                                        <div class="notes-label">Student notes</div>
                                        <div v-if="req.notes" class="notes-text">
                                            {{ req.notes }}
                                        </div>
                                        <div v-else class="notes-empty grey--text text--darken-1">
                                            No notes provided.
                                        </div>
                                    </div>
                                </div>

                                <div class="request-card-layout__actions">
                                    <div class="request-actions request-actions--card">
                                        <v-btn v-if="req.studentId" text small color="primary" class="availability-btn"
                                            :to="{ name: 'StudentProfile', params: { id: req.studentId } }">
                                            <v-icon left small>mdi-calendar-outline</v-icon>
                                            View Student Availability
                                        </v-btn>
                                        <div class="request-actions-buttons">
                                            <v-btn small outlined color="error" class="action-btn"
                                                @click.stop="rejectRequest(req)">
                                                <v-icon left small>mdi-close</v-icon>
                                                Reject
                                            </v-btn>
                                            <v-btn small color="primary" class="action-btn"
                                                @click.stop="approveRequest(req)">
                                                <v-icon left small>mdi-calendar-outline</v-icon>
                                                Schedule
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>

                    <div v-if="hasMore" class="text-center py-3">
                        <v-btn v-if="!loadingMore" small outlined @click="loadMore">Load more</v-btn>
                        <v-progress-circular v-else indeterminate size="24" color="primary" />
                    </div>
                </template>
            </v-container>
        </div>

        <BookingModal v-model="showApprovalModal" :key="selectedRequest ? selectedRequest.id : 'approve-modal'"
            :appointment="selectedRequest" :remaining-hours="999" :payment-method-added="true" mode="approve"
            @submit="submitApprovedBooking" @error="(msg) => $toast.error(msg)" />
    </div>
</template>

<script>
import Parse from 'parse'
import { mapActions } from 'vuex'
import BookingModal from '@/components/dashboard/BookingModal.vue'
import { iPhoneVersion, isIphone } from '@/utils'

export default {
    name: 'InstructorRequestedBookings',
    components: {
        BookingModal
    },
    data() {
        return {
            loading: false,
            loadingMore: false,
            errorMessage: '',
            requests: [],
            pageSize: 5,
            hasMore: true,
            showApprovalModal: false,
            selectedRequest: null
        }
    },
    computed: {
        wrapperStyle() {
            const mobile = this.$vuetify.breakpoint.mobile
            if (mobile) {
                return {
                    position: 'relative',
                    overflow: 'auto'
                }
            }
            const height = 92 + (this.isIphone10Avobe() ? 34 : 0)
            return {
                position: 'relative',
                overflow: 'auto',
                height: `calc(100vh - ${height}px)`
            }
        }
    },
    async created() {
        await this.loadRequests(false)
    },
    methods: {
        ...mapActions(['sendPushNotification']),
        isIphone10Avobe() {
            return (
                isIphone() &&
                ['iPhone-X-Xs', 'iPhone-Xmax-Xr', 'iPhone-12'].includes(iPhoneVersion())
            )
        },
        formatWhen(date) {
            try {
                if (!date) return '—'
                if (this.$moment) return this.$moment(date).fromNow()
            } catch (e) { }
            return new Date(date).toLocaleString()
        },
        mapRowToRequest(row) {
            const student = row.get('studentId')
            const first = student?.get?.('firstName') || ''
            const last = student?.get?.('lastName') || ''
            const name = `${first} ${last}`.trim() || 'Student'
            return {
                id: row.id,
                createdAt: row.createdAt,
                notes: row.get('notes') || '',
                hoursLesson: Number(row.get('hoursLesson') || 1),
                start: row.get('start') || null,   // optional prefill
                end: row.get('end') || null,
                studentId: student?.id || '',
                studentName: name,
                studentEmail: student?.get?.('customEmail') || '',
                studentAvatar: student?.get?.('profilePicture') || ''
            }
        },
        isBookingLessonRequest(row) {
            const t = row.get('type')
            if (Array.isArray(t)) return t.includes('booking_lesson')
            return t === 'booking_lesson'
        },
        async loadRequests(append = false) {
            const instructor = Parse.User.current()
            if (!instructor) return

            if (append) {
                if (this.loadingMore || !this.hasMore) return
                this.loadingMore = true
            } else {
                this.loading = true
                this.errorMessage = ''
            }
            try {
                const Appointment = Parse.Object.extend('Appointments')
                const query = new Parse.Query(Appointment)
                query.equalTo('instructorId', instructor)
                query.equalTo('status', 'Pending')
                query.equalTo('type', 'booking_lesson')
                query.include('studentId')
                query.descending('createdAt')
                query.limit(this.pageSize)
                query.skip(append ? this.requests.length : 0)

                let rows = await query.find()
                rows = rows.filter((row) => this.isBookingLessonRequest(row))

                const mapped = rows.map((row) => this.mapRowToRequest(row))

                if (append) {
                    this.requests = this.requests.concat(mapped)
                } else {
                    this.requests = mapped
                }
                this.hasMore = rows.length >= this.pageSize
            } catch (err) {
                console.error('loadRequests error:', err)
                this.errorMessage = 'Unable to load requested bookings.'
                if (!append) this.requests = []
                this.hasMore = false
            } finally {
                if (append) {
                    this.loadingMore = false
                } else {
                    this.loading = false
                }
            }
        },
        loadMore() {
            this.loadRequests(true)
        },
        onScroll(e) {
            if (this.loading || this.loadingMore || !this.hasMore) return
            const el = e?.target
            if (!el || typeof el.scrollHeight === 'undefined') return
            if (el.scrollHeight - el.scrollTop - el.clientHeight > 200) return
            this.loadMore()
        },
        approveRequest(req) {
            this.selectedRequest = req
            this.showApprovalModal = true
        },
        async submitApprovedBooking(formData) {
            if (!this.selectedRequest?.id) {
                this.$toast?.error?.('No request selected.')
                return
            }

            try {
                const Appointment = Parse.Object.extend('Appointments')
                const query = new Parse.Query(Appointment)
                query.include('studentId')
                const appointment = await query.get(this.selectedRequest.id)

                const lessonHours = Number(formData.lessonHours || 1)
                const start = new Date(`${formData.date}T${formData.startTime}:00`)
                const end = new Date(start)
                end.setHours(end.getHours() + lessonHours)

                appointment.set('status', 'Approved')
                appointment.set('start', start)
                appointment.set('end', end)
                appointment.set('hoursLesson', String(lessonHours))
                appointment.set('notes', formData.notes || '')

                const instructor = Parse.User.current()
                if (instructor) {
                    const instructorPackage = await new Parse.Query('InstructorPackages')
                        .equalTo('instructorId', instructor)
                        .first()
                    if (instructorPackage) {
                        const instructorPackagePointer = new Parse.Object('InstructorPackages')
                        instructorPackagePointer.id = instructorPackage.id
                        appointment.set('instructorPackageId', instructorPackagePointer)
                    }
                }

                await appointment.save()

                this.$toast?.success?.('Booking approved and scheduled.')
                this.showApprovalModal = false
                this.selectedRequest = null

                // remove from pending list OR reload
                this.requests = this.requests.filter(r => r.id !== appointment.id)

                try {
                    await this.notifyStudentBookingApproved(appointment, start)
                } catch (notifyErr) {
                    console.warn('notifyStudentBookingApproved:', notifyErr)
                }
            } catch (err) {
                console.error('submitApprovedBooking error:', err)
                this.$toast?.error?.(err?.message || 'Failed to approve booking.')
            }
        },
        async notifyStudentBookingApproved(appointment, start) {
            const instructor = Parse.User.current()
            if (!instructor) return

            const student = appointment.get('studentId')
            if (!student) return

            const studentName =
                `${student.get('firstName') || ''} ${student.get('lastName') || ''}`.trim() ||
                'Student'
            const instructorName =
                `${instructor.get('firstName') || ''} ${instructor.get('lastName') || ''}`.trim() ||
                'Instructor'

            const displayLine = this.$moment
                ? this.$moment(start).format('LLL')
                : start.toLocaleString()

            const deviceId = student.get('deviceId')
            if (typeof deviceId !== 'undefined' && deviceId) {
                await this.sendPushNotification({
                    player_id: deviceId,
                    message:
                        'Appointment approved on ' +
                        (start ? displayLine : 'your requested date'),
                    type: 'noti',
                    senderId: instructor.id,
                    name: instructorName
                })
            }

            const studentPhone = student.get('phone')
            if (studentPhone) {
                const smsMessage =
                    instructorName +
                    ' has approved your requested appointment on ' +
                    displayLine +
                    ' Please log in to the DriverX app for more details.'
                await Parse.Cloud.run('sendAppointmentSMS', {
                    message: smsMessage,
                    to: studentPhone
                })
            }

            const studentEmail = student.get('customEmail')
            if (studentEmail) {
                Parse.Cloud.run('approvedEmail', {
                    name: studentName,
                    instructorName,
                    date: start,
                    email: studentEmail
                }).catch((e) => console.warn('approvedEmail:', e))
            }
        },
        async rejectRequest(req) {
            const Appointment = Parse.Object.extend('Appointments')
            const appointment = await new Parse.Query(Appointment).get(req.id)
            appointment.set('status', 'Declined')
            await appointment.save()
            this.requests = this.requests.filter(r => r.id !== req.id)
        }
    }
}
</script>

<style scoped>
.requested-bookings-page {
    min-height: 100%;
    display: flex;
    flex-direction: column;
}

.requested-bookings-scroll.rounded {
    overflow: auto;
    -webkit-overflow-scrolling: touch;
}

.requested-bookings-scroll--mobile.rounded {
    padding-top: constant(safe-area-inset-top, 0px);
    padding-top: env(safe-area-inset-top, 0px);
    height: calc(100vh - 88px - constant(safe-area-inset-top, 0px)) !important;
    height: calc(100vh - 88px - env(safe-area-inset-top, 0px)) !important;
}

.page-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.page-title {
    font-size: 1.35rem;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
}

.page-subtitle {
    margin-top: 6px;
    font-size: 0.9rem;
    color: #64748b;
    max-width: 48rem;
}

.request-card {
    border-radius: 12px;
    background: #ffffff;
}

.request-card-horizontal {
    width: 100%;
}

.request-card-layout {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

@media (min-width: 960px) {
    .request-card-layout {
        flex-direction: row;
        align-items: stretch;
        gap: 20px;
    }

    .request-card-layout__identity {
        flex: 0 1 300px;
        min-width: 0;
    }

    .request-card-layout__notes {
        flex: 1 1 0;
        min-width: 0;
    }

    .request-card-layout__actions {
        flex: 0 0 auto;
        width: 220px;
        min-width: 180px;
    }

    .request-actions--card {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        margin-top: 0 !important;
        height: 100%;
        justify-content: flex-end;
    }

    .request-actions--card .request-actions-buttons {
        justify-content: stretch;
        gap: 8px;
    }

    .request-actions--card .action-btn {
        flex: 1 1 auto;
        min-width: 0;
    }
}

.request-title-row {
    display: flex;
    align-items: center;
    min-width: 0;
}

.request-student-name {
    font-weight: 700;
    color: #0f172a;
    font-size: 1rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-student-link {
    text-decoration: none;
    color: inherit;
    border-bottom: 1px solid transparent;
    transition: color 0.15s ease, border-color 0.15s ease;
}

.request-student-link:hover {
    color: #2d9a47;
    border-bottom-color: rgba(45, 154, 71, 0.45);
}

.avatar-link {
    text-decoration: none;
    border-radius: 50%;
    display: inline-flex;
}

.avatar-link:focus {
    outline: 2px solid rgba(45, 154, 71, 0.5);
    outline-offset: 2px;
}

.request-avatar {
    cursor: pointer;
}

.request-email {
    display: flex;
    align-items: center;
    min-width: 0;
    word-break: break-all;
}

.request-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
}

.availability-btn {
    align-self: flex-start;
    max-width: 100%;
    height: auto !important;
    white-space: normal;
    text-align: left;
}

.request-actions-buttons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    width: 100%;
}

.action-btn {
    min-width: 0;
    flex: 0 1 auto;
}

@media (min-width: 600px) {
    .request-actions {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
    }

    .request-actions-buttons {
        width: auto;
        flex: 1 1 auto;
        justify-content: flex-end;
        min-width: 200px;
    }
}

@media (max-width: 599px) {
    .request-actions-buttons {
        justify-content: stretch;
    }

    .request-actions-buttons .action-btn {
        flex: 1 1 calc(50% - 4px);
        min-width: 120px;
    }
}

.request-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    color: #475569;
    font-size: 0.82rem;
}

.meta-item {
    display: inline-flex;
    align-items: center;
}

.meta-dot {
    opacity: 0.6;
}

.request-created {
    font-size: 0.78rem;
}

.notes-block {
    background: rgba(15, 23, 42, 0.04);
    border-radius: 10px;
    padding: 10px 12px;
}

.notes-label {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 700;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.notes-text {
    font-size: 0.92rem;
    color: #0f172a;
    white-space: pre-wrap;
    word-break: break-word;
}

.notes-empty {
    font-size: 0.9rem;
}

.empty-state {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
}

@media (max-width: 959px) {
    .page-title {
        font-size: 1.15rem;
    }

    .requested-bookings-scroll .v-container {
        padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px) + 16px);
    }
}
</style>
